import { useRef, useState } from "react";
import { toast } from "sonner";
import { IoScanOutline, IoImageOutline, IoCameraOutline, IoRefreshOutline } from "react-icons/io5";
import api from "@/api";
import { DateInput } from "@/components/DateInput";
import { compressImage } from "@/utils/image";

/**
 * DateInput + OCR button. Same visual language as the barcode scanner
 * button on the ExpiryForm. Uses Gemini Vision via POST /api/ocr/expiry-date.
 * Props: value, onChange, testIdPrefix (default "expiry-date")
 */
export function ExpiryDateInput({ value, onChange, testIdPrefix = "expiry-date" }) {
  const [ocrLoading, setOcrLoading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [lastFile, setLastFile] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const runOcr = async (rawFile) => {
    if (!rawFile) return;
    if (!/^image\/(jpe?g|png|webp|heic|heif)$/i.test(rawFile.type) && rawFile.type !== "") {
      toast.error("Formato non supportato (JPEG/PNG/WEBP)");
      return;
    }
    setLastFile(rawFile);
    setErrorMsg("");
    setOcrLoading(true);
    try {
      // Compress large images to speed up upload on cellular data
      const file = await compressImage(rawFile, { maxSide: 1600, quality: 0.82 });
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await api.post("/ocr/expiry-date", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      if (data?.expiry_date) {
        onChange(data.expiry_date);
        toast.success(`Data rilevata: ${data.expiry_date}`);
        setSheetOpen(false);
        setLastFile(null);
      } else {
        setErrorMsg("Data non rilevata nella foto. Riprova con una foto più nitida o inseriscila a mano.");
      }
    } catch (e) {
      const status = e?.response?.status;
      let msg;
      if (status === 402) msg = e?.response?.data?.detail || "Credito OCR esaurito";
      else if (status === 503) msg = "OCR non disponibile in questo momento";
      else if (status === 415) msg = "Formato immagine non supportato";
      else if (!navigator.onLine) msg = "OCR richiede connessione internet";
      else if (e?.code === "ECONNABORTED") msg = "OCR troppo lento — riprova con foto più piccola";
      else msg = "OCR fallito. Riprova.";
      setErrorMsg(msg);
    } finally {
      setOcrLoading(false);
    }
  };

  const retry = () => { if (lastFile) runOcr(lastFile); };

  const closeSheet = () => {
    if (ocrLoading) return;
    setSheetOpen(false);
    setLastFile(null);
    setErrorMsg("");
  };

  return (
    <>
      <div className="tf-input-wrap">
        <div style={{ paddingRight: 48 }}>
          <DateInput data-testid={`${testIdPrefix}-input`} value={value} onChange={onChange} />
        </div>
        <button
          type="button"
          data-testid={`${testIdPrefix}-ocr-btn`}
          onClick={() => setSheetOpen(true)}
          disabled={ocrLoading}
          className="tf-icon-btn red"
          style={{ position: "absolute", right: 5, top: "50%", transform: "translateY(-50%)", width: 40, height: 40 }}
          aria-label="Rileva data da foto"
          title="Rileva data da foto"
        >
          <IoScanOutline size={20} />
        </button>
      </div>

      <input
        ref={cameraRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        data-testid={`${testIdPrefix}-ocr-camera-input`}
        style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; runOcr(f); }}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        data-testid={`${testIdPrefix}-ocr-gallery-input`}
        style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; runOcr(f); }}
      />

      {(sheetOpen || ocrLoading) && (
        <div
          data-testid={`${testIdPrefix}-ocr-sheet`}
          onClick={closeSheet}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000,
            display: "flex", alignItems: "flex-end", justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: 480, background: "var(--tf-surface, #fff)",
              color: "var(--tf-text, #111)", borderTopLeftRadius: 20, borderTopRightRadius: 20,
              padding: 20, paddingBottom: "calc(20px + env(safe-area-inset-bottom))",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Rileva data di scadenza</div>
            <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 16 }}>
              Scatta una foto ravvicinata e a fuoco della data stampata sul prodotto.
            </div>
            {ocrLoading ? (
              <div data-testid={`${testIdPrefix}-ocr-loading`} style={{ textAlign: "center", padding: 24 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Analisi in corso…</div>
                <div style={{ fontSize: 12, opacity: 0.65 }}>Sto leggendo la data con l&apos;IA</div>
              </div>
            ) : errorMsg ? (
              <div style={{ display: "grid", gap: 10 }}>
                <div
                  data-testid={`${testIdPrefix}-ocr-error`}
                  style={{
                    background: "var(--tf-red-soft)",
                    color: "var(--tf-red)",
                    padding: "12px 14px",
                    borderRadius: 12,
                    fontSize: 13,
                    lineHeight: 1.4,
                  }}
                >
                  {errorMsg}
                </div>
                {lastFile && (
                  <button
                    type="button"
                    data-testid={`${testIdPrefix}-ocr-retry`}
                    className="tf-btn-primary"
                    onClick={retry}
                  >
                    <IoRefreshOutline size={18} /> Riprova con la stessa foto
                  </button>
                )}
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-ocr-camera`}
                  className="tf-btn-ghost"
                  onClick={() => { setErrorMsg(""); cameraRef.current?.click(); }}
                >
                  <IoCameraOutline size={18} /> Scatta nuova foto
                </button>
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-ocr-cancel`}
                  className="tf-btn-ghost"
                  onClick={closeSheet}
                >
                  Chiudi
                </button>
              </div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-ocr-camera`}
                  className="tf-btn-primary"
                  onClick={() => cameraRef.current?.click()}
                >
                  <IoCameraOutline size={18} /> Scatta foto
                </button>
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-ocr-gallery`}
                  className="tf-btn-ghost"
                  onClick={() => galleryRef.current?.click()}
                >
                  <IoImageOutline size={18} /> Carica da galleria
                </button>
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-ocr-cancel`}
                  className="tf-btn-ghost"
                  onClick={closeSheet}
                >
                  Annulla
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
